import { HttpParams } from '@angular/common/http';
import { CAMPAIGN_COL_ENUM } from '@core/enum/marketing-campaign';
import { GetLeadBody, GetLeadParams, LeadFilters, LeadQuickFilters } from '@core/models/interfaces/lead';
import { normalizePhoneNumberKeyword } from '@shared/utils/string';
import moment from 'moment';
import { TreeNode } from 'primeng/api';

export function getParamsFilterLeads(
  filters: LeadFilters,
  quickFilters: LeadQuickFilters,
  keyword: string,
  pagination: { page: number; size: number },
  sorts: string,
  userGroupsSelected:TreeNode[]
): GetLeadParams {
  const { page, size } = pagination;

  const {
    agents,
    states,
    source,
    subSource,
    currentUser,
    genders,
    csr,
    leadTags,
    smsCampaign,
    leadNeeds,
    leadPainPoints,
  } = filters;
  const { leadStatus, birthday, dates, roles, update, dateAdded, leadTypes, leadStages, lastInteractionAt } =
    quickFilters;

  const dateAddedFrom = dateAdded?.[0] ? moment(dateAdded[0]).startOf('date').toDate().toISOString() : undefined;
  const dateAddedTo = dateAdded?.[1] ? moment(dateAdded[1]).endOf('date').toDate().toISOString() : undefined;
  // Birthday: lấy đúng ngày user chọn, start 00:00 end 23:59:59.999 theo UTC (không phụ thuộc múi giờ user)
  const birthdayFrom = birthday?.[0]
    ? moment.utc({ y: moment(birthday[0]).year(), M: moment(birthday[0]).month(), D: moment(birthday[0]).date() })
        .startOf('day')
        .toISOString()
    : undefined;
  const birthdayTo = birthday?.[1]
    ? moment.utc({ y: moment(birthday[1]).year(), M: moment(birthday[1]).month(), D: moment(birthday[1]).date() })
        .startOf('day')
        .toISOString()
    : undefined;

  const lastUpdateFrom =
    update === 'LAST_UPDATE' && dates?.[0] ? moment(dates[0]).startOf('date').toDate().toISOString() : undefined;
  const lastUpdateTo =
    update === 'LAST_UPDATE' && dates?.[1]
      ? moment(dates[1]).endOf('date').toDate().toISOString()
      : lastUpdateFrom
        ? moment().toDate().toISOString()
        : undefined;

  const noUpdateFrom =
    update === 'NO_UPDATE' && dates[0] ? moment(dates[0]).startOf('date').toDate().toISOString() : undefined;
  const noUpdateTo =
    update === 'NO_UPDATE' && dates?.[1]
      ? moment(dates[1]).endOf('date').toDate().toISOString()
      : noUpdateFrom
        ? moment().toDate().toISOString()
        : undefined;

  const currentUserIds = currentUser.map(item => item.id);
  const bodyUserGroup = getParentUserIds(userGroupsSelected);
  // gộp + loại bỏ trùng

  // check phone number
  keyword = normalizePhoneNumberKeyword(keyword)

  return {
    page,
    size,
    currentUserIds: currentUserIds.length ? currentUserIds : undefined,
    leadStatusIds: leadStatus.length ? leadStatus : undefined,
    agentIds: agents.map(item => item.id),
    states: states,
    leadSourceIds: source.map(item => item.id),
    subSourceIds: subSource.map(item => item.id),
    leadStageIds: leadStages,
    keyword: keyword,
    birthdayFrom,
    birthdayTo,
    lastUpdateFrom,
    lastUpdateTo,
    noUpdateFrom,
    noUpdateTo,
    from: dateAddedFrom,
    to: dateAddedTo,
    leadTypes: leadTypes,
    genders: genders.map(item => item.value),
    csrIds: csr.map(item => item.id),
    roles: roles,
    sorts: keyword ? '' : sorts || '-createdAt',
    leadTagIds: leadTags.map(item => item.id),
    smsCampaignIds: smsCampaign.map((item: any) => item.id),
    lastInteractionDays: lastInteractionAt,
    // leadPainPointIds: leadPainPoints.map(item => item.id),
    // leadNeedsIds: leadNeeds.map(item => item.id)
    bodyRequired: bodyUserGroup
  };
}

function getParentUserIds(treeNodes: any[]): GetLeadBody {
  const resultMembers = new Set<string>();
  const resultViewers = new Set<string>();
  const resultGroupIds = new Set<string>();

  treeNodes.forEach(node => {
    // chỉ lấy ở parent (level root)
    const memberIds = node?.data?.memberIds ?? [];
    const viewerIds = node?.data?.viewerIds ?? [];
    memberIds.forEach((id: string) => resultMembers.add(id));
    viewerIds.forEach((id: string) => resultViewers.add(id));
    resultGroupIds.add(node?.data?.id);
  });

  const temp: GetLeadBody = {
    memberIds: Array.from(resultMembers),
    viewerIds: Array.from(resultViewers),
    groupIds: Array.from(resultGroupIds)
  };

  return temp;
}

export function parserParamsToAPILeads(params?: GetLeadParams) {
  let queryParams = new HttpParams();

  if (params?.keyword !== undefined) {
    queryParams = queryParams.append('keyword', params?.keyword);
  }
  if (params?.currentUserIds) {
    for (let id of params?.currentUserIds) {
      queryParams = queryParams.append('currentUserIds', id);
    }
  }
  if (params?.leadStatusIds) {
    for (let id of params?.leadStatusIds) {
      queryParams = queryParams.append('leadStatusIds', id);
    }
  }
  if (params?.states) {
    for (let id of params?.states) {
      queryParams = queryParams.append('states', id);
    }
  }
  if (params?.leadSourceIds) {
    for (let id of params?.leadSourceIds) {
      queryParams = queryParams.append('leadSourceIds', id);
    }
  }
  if (params?.subSourceIds) {
    for (let id of params?.subSourceIds) {
      queryParams = queryParams.append('subSourceIds', id);
    }
  }
  if (params?.agentIds) {
    for (let id of params?.agentIds) {
      queryParams = queryParams.append('agentIds', id);
    }
  }
  if (params?.from !== undefined) {
    queryParams = queryParams.append('from', params?.from);
  }
  if (params?.to !== undefined) {
    queryParams = queryParams.append('to', params?.to);
  }
  if (params?.page !== undefined) {
    queryParams = queryParams.append('page', params?.page);
  }
  if (params?.size !== undefined) {
    queryParams = queryParams.append('size', params?.size);
  }
  if (params?.sorts !== undefined) {
    queryParams = queryParams.append('sorts', params?.sorts);
  }
  //BIRTHDAY
  if (params?.birthdayFrom !== undefined) {
    queryParams = queryParams.append('birthdayFrom', params?.birthdayFrom);
  }
  if (params?.birthdayTo !== undefined) {
    queryParams = queryParams.append('birthdayTo', params?.birthdayTo);
  }
  // LAST UPDATE
  if (params?.lastUpdateFrom !== undefined) {
    queryParams = queryParams.append('lastUpdateFrom', params?.lastUpdateFrom);
  }
  if (params?.lastUpdateTo !== undefined) {
    queryParams = queryParams.append('lastUpdateTo', params?.lastUpdateTo);
  }
  // NO UPDATE
  if (params?.noUpdateFrom !== undefined) {
    queryParams = queryParams.append('noUpdateFrom', params?.noUpdateFrom);
  }
  if (params?.noUpdateTo !== undefined) {
    queryParams = queryParams.append('noUpdateTo', params?.noUpdateTo);
  }
  if (params?.leadTypes) {
    for (let id of params?.leadTypes) {
      queryParams = queryParams.append('leadTypes', id);
    }
  }
  if (params?.genders) {
    for (let id of params?.genders) {
      queryParams = queryParams.append('genders', id);
    }
  }
  if (params?.csrIds) {
    for (let id of params?.csrIds) {
      queryParams = queryParams.append('csrIds', id);
    }
  }
  if (params?.roles) {
    queryParams = queryParams.append('roles', params?.roles);
  }

  if (params?.leadStageIds) {
    for (let id of params?.leadStageIds) {
      queryParams = queryParams.append('leadStageIds', id);
    }
  }

  if (params?.leadTagIds) {
    for (let id of params?.leadTagIds) {
      queryParams = queryParams.append('leadTagIds', id);
    }
  }

  if (params?.smsCampaignIds) {
    for (let id of params?.smsCampaignIds) {
      queryParams = queryParams.append('smsCampaignIds', id);
    }
  }

  if (params?.lastInteractionDays) {
    queryParams = queryParams.append('lastInteractionDays', params?.lastInteractionDays);
  }

  // if (params?.leadNeedsIds) {
  //   for (let id of params?.leadNeedsIds) {
  //     queryParams = queryParams.append('leadNeedsIds', id);
  //   }
  // }

  // if (params?.leadPainPointIds) {
  //   for (let id of params?.leadPainPointIds) {
  //     queryParams = queryParams.append('leadPainPointIds', id);
  //   }
  // }

  //ACTION
  if (params?.actionChange) {
    queryParams = queryParams.append('actionChange', params.actionChange);
  }

  if (params?.attributeId) {
    queryParams = queryParams.append('attributeId', params.attributeId);
  }

  if (params?.transferRela) {
    queryParams = queryParams.append('transferRela', params.transferRela);
  }

  return queryParams;
}

export function getTooltipHeader(type: string): string | undefined {
  const headerMap: Record<string, string> = {
    [CAMPAIGN_COL_ENUM.ON_OFF]: 'on_off',
    [CAMPAIGN_COL_ENUM.CAMPAIGN]: 'campaign',
    [CAMPAIGN_COL_ENUM.CAMPAIGN_ID]: 'setting.campaign_id',
    [CAMPAIGN_COL_ENUM.OBJECTIVE]: 'setting.objective',
    [CAMPAIGN_COL_ENUM.PAGE_NAME]: 'setting.page_name',
    [CAMPAIGN_COL_ENUM.DELIVERY]: 'delivery',
    [CAMPAIGN_COL_ENUM.BUDGET]: 'budget',
    [CAMPAIGN_COL_ENUM.AMOUNT_SPENT]: 'amount_spent',
    [CAMPAIGN_COL_ENUM.IMPRESSIONS]: 'impressions',
    [CAMPAIGN_COL_ENUM.CPM]: 'cpm',
    [CAMPAIGN_COL_ENUM.REACH]: 'reach',
    [CAMPAIGN_COL_ENUM.FREQUENCY]: 'frequency',
    [CAMPAIGN_COL_ENUM.RESULTS]: 'results',
    [CAMPAIGN_COL_ENUM.COST_PER_RESULT]: 'cost_per_result',
    [CAMPAIGN_COL_ENUM.RESULT_RATE]: 'result_rate',
    [CAMPAIGN_COL_ENUM.LINK_CLICKS]: 'link_clicks',
    [CAMPAIGN_COL_ENUM.CPC_ALL]: 'cpc_all',
    [CAMPAIGN_COL_ENUM.CTR_ALL]: 'ctr_all',
    [CAMPAIGN_COL_ENUM.UNIQUE_CTR]: 'unique_ctr',
    [CAMPAIGN_COL_ENUM.VIDEO_AVERAGE_PLAY_TIME]: 'video_average_play_time',
    [CAMPAIGN_COL_ENUM.POST_ENGAGEMENTS]: 'post_engagements',
    [CAMPAIGN_COL_ENUM.ROAS]: 'roas',
    [CAMPAIGN_COL_ENUM.CLICKS_ALL]: 'clicks_all',
    [CAMPAIGN_COL_ENUM.UNIQUE_CLICKS]: 'unique_clicks_all',
    [CAMPAIGN_COL_ENUM.OUTBOUND_CLICKS]: 'outbound_clicks',
    [CAMPAIGN_COL_ENUM.UNIQUE_OUTBOUND_CLICKS]: 'unique_outbound_clicks',
    [CAMPAIGN_COL_ENUM.CTR]: 'ctr',
    [CAMPAIGN_COL_ENUM.CPC]: 'cpc',
    [CAMPAIGN_COL_ENUM.LANDING_PAGE_VIEWS]: 'landing_page_views',
    [CAMPAIGN_COL_ENUM.WEBSITE_LEADS]: 'website_lead',
    [CAMPAIGN_COL_ENUM.UNIQUE_LINK_CLICKS]: 'unique_link_clicks',
    [CAMPAIGN_COL_ENUM.COST_PER_UNIQUE_LINK_CLICK]: 'cost_per_unique_link_click',
    [CAMPAIGN_COL_ENUM.POST_COMMENTS]: 'post_comment',
    [CAMPAIGN_COL_ENUM.NEW_MESSAGING_CONTACTS]: 'new_messaging_contacts',
    [CAMPAIGN_COL_ENUM.RETURNING_MESSAGING_CONTACTS]: 'return_messaging_contact',
    [CAMPAIGN_COL_ENUM.MESSAGING_CONVERSATIONS_REPLIED]: 'messaging_conversations_replied',
    [CAMPAIGN_COL_ENUM.MESSAGING_CONVERSATIONS_STARTED]: 'messaging_conversations_started',
    [CAMPAIGN_COL_ENUM.MESSAGING_CONTACTS]: 'messaging_contact',
    [CAMPAIGN_COL_ENUM.BLOCKS]: 'block',
    [CAMPAIGN_COL_ENUM.BLOCK]: 'block',
    [CAMPAIGN_COL_ENUM.POST_REACTIONS]: 'post_reactions',
    [CAMPAIGN_COL_ENUM.END]: 'setting.end',
    [CAMPAIGN_COL_ENUM.SCHEDULED]: 'setting.schedule',
    [CAMPAIGN_COL_ENUM.POST_SAVES]: 'post_saves',
    [CAMPAIGN_COL_ENUM.POST_SHARES]: 'post_share',
    [CAMPAIGN_COL_ENUM.PAGE_ENGAGEMENT]: 'page_engagement',
    [CAMPAIGN_COL_ENUM.CPP]: 'cpp',
    [CAMPAIGN_COL_ENUM.FOLLOWS_OR_LIKES]: 'follow_like',
    [CAMPAIGN_COL_ENUM.OUTBOUND_CTR]: 'outbound_ctr',
    [CAMPAIGN_COL_ENUM.UNIQUE_OUTBOUND_CTR]: 'unique_outbound_ctr',
    [CAMPAIGN_COL_ENUM.UNIQUE_CTR_ALL]: 'unique_ctr_all',
    [CAMPAIGN_COL_ENUM.THREE_SECOND_VIDEO_PLAYS]: '3-second_video_plays',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS_AT_25]: 'video_play_25%',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS_AT_50]: 'video_play_50%',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS_AT_75]: 'video_play_75%',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS_AT_95]: 'video_play_95%',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS_AT_100]: 'video_play_100%',
    [CAMPAIGN_COL_ENUM.VIDEO_PLAYS]: 'videos_play',
    [CAMPAIGN_COL_ENUM.THRUPLAYS]: 'thruplays',
    [CAMPAIGN_COL_ENUM.HEADLINE_AD_SETTINGS]: 'setting.headline_ad_settings',
    [CAMPAIGN_COL_ENUM.BODY_AD_SETTINGS]: 'setting.body_ad_settings',
    [CAMPAIGN_COL_ENUM.LINK_AD_SETTINGS]: 'setting.link_ad_settings',
    [CAMPAIGN_COL_ENUM.STARTS]: 'setting.starts',
    [CAMPAIGN_COL_ENUM.ENDS]: 'setting.ends',
    [CAMPAIGN_COL_ENUM.AD_NAME]: 'setting.ad_name',
    [CAMPAIGN_COL_ENUM.AD_SET_ID]: 'setting.ad_set_id',
    [CAMPAIGN_COL_ENUM.AD_ID]: 'setting.ad_id',
    [CAMPAIGN_COL_ENUM.AD_SET_NAME]: 'setting.ad_set_name'
  };

  return headerMap[type];
}
